import type { ISO8601DateOnly } from '../../../cross-app-shared/types/common.ts';
import { dateUtc, dateToLocaleDateString } from '../../../cross-app-shared/services/dates.ts';
import type { EmissionsTrackingFrequency, EmissionsPeriod } from '../../types/db.ts';

export interface EmissionsPeriodDetailsBase {
  start: ISO8601DateOnly;
  end: ISO8601DateOnly;
  friendly: string;
}

export interface EmissionsPeriodDetailsNormal extends EmissionsPeriodDetailsBase {
  type: 'normal';
  period: EmissionsPeriod;
  frequency: EmissionsTrackingFrequency;
  rollingRangeFriendly: string;
  index: number;
}

export interface EmissionsPeriodDetailsSubPeriod extends EmissionsPeriodDetailsBase {
  type: 'subPeriod';
  period: EmissionsPeriod;
  parentPeriod: EmissionsPeriodDetailsNormal;
}

export interface EmissionsPeriodDetailsPotential extends EmissionsPeriodDetailsBase {
  type: 'potential';
  period: 'potential';
}

export type EmissionsPeriodDetails =
  | EmissionsPeriodDetailsNormal
  | EmissionsPeriodDetailsSubPeriod
  | EmissionsPeriodDetailsPotential;

export function periodPotential(today: ISO8601DateOnly): EmissionsPeriodDetailsPotential {
  return { type: 'potential', period: 'potential', start: today, end: today, friendly: 'PTE' };
}

export function subPeriodKey(
  parentPeriod: EmissionsPeriod,
  start: ISO8601DateOnly,
  end: ISO8601DateOnly,
): EmissionsPeriod {
  return `${parentPeriod}@${start}_${end}` as EmissionsPeriod;
}

/**
 * Splits a parent period into sub-periods at the given split dates.
 * Split dates define the start of each new sub-period after the first.
 * For example, a monthly period Mar 2024 with splitDates=['2024-03-15'] produces:
 *   [Mar 1-14, Mar 15-31]
 *
 * @param parent The parent period details
 * @param splitDates Sorted ISO8601 date strings where each split begins. Must be within the parent period range.
 * @returns Array of sub-period details
 */
export function periodSplitIntoSubPeriods(
  parent: EmissionsPeriodDetailsNormal,
  splitDates: ISO8601DateOnly[],
): EmissionsPeriodDetailsSubPeriod[] {
  if (splitDates.length === 0) {
    return [];
  }

  const boundaries: ISO8601DateOnly[] = [parent.start, ...splitDates];
  const subPeriods: EmissionsPeriodDetailsSubPeriod[] = [];

  for (let i = 0; i < boundaries.length; i++) {
    const start = boundaries[i];
    let end: ISO8601DateOnly;

    if (i < boundaries.length - 1) {
      // End is one day before the next boundary
      const nextBoundary = new Date(boundaries[i + 1] + 'T00:00:00Z');
      nextBoundary.setUTCDate(nextBoundary.getUTCDate() - 1);
      end = dateUtc(nextBoundary);
    } else {
      end = parent.end;
    }

    const startFriendly = dateToLocaleDateString(start);
    const endFriendly = dateToLocaleDateString(end);
    const friendly = start === end ? startFriendly : `${startFriendly} - ${endFriendly}`;

    subPeriods.push({
      type: 'subPeriod',
      period: subPeriodKey(parent.period, start, end),
      start,
      end,
      friendly,
      parentPeriod: parent,
    });
  }

  return subPeriods;
}
const periodsPrecomputed: {
  [Key in EmissionsTrackingFrequency]: EmissionsPeriodDetailsNormal[];
} = {
  daily: [],
  weekly: [],
  monthly: [],
  quarterly: [],
  semiannually: [],
  annually: [],
};

// Precompute all the intervals:
{
  const push = (
    frequency: EmissionsTrackingFrequency,
    details: Omit<EmissionsPeriodDetailsNormal, 'type' | 'friendly' | 'rollingRangeFriendly' | 'frequency' | 'index'>,
  ) => {
    let friendly: string;

    if (frequency === 'monthly') {
      friendly = new Date(details.start).toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      });
    } else if (frequency === 'annually') {
      friendly = details.period;
    } else {
      const startFriendly = dateToLocaleDateString(details.start);

      if (details.start === details.end) {
        friendly = startFriendly;
      } else {
        const endFriendly = dateToLocaleDateString(details.end);

        friendly = `${startFriendly} - ${endFriendly}`;
      }
    }

    const detailsPushing: EmissionsPeriodDetails = {
      ...details,
      type: 'normal',
      frequency,
      friendly,
      rollingRangeFriendly: '',
      index: periodsPrecomputed[frequency].length,
    };
    periodsPrecomputed[frequency].push(detailsPushing);
    detailsPushing.rollingRangeFriendly = periodToRollingRange(detailsPushing);
  };

  // We actually want to start in 2001, but that year didn't start on a Sunday, so we'll start on Sunday to make sure
  // all our weekly entries are full:
  const tomorrow = new Date();
  tomorrow.setUTCHours(0, 0, 0, 0);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  for (const current = new Date('2000-12-31'); current < tomorrow; current.setUTCDate(current.getUTCDate() + 1)) {
    const currentDateStr = dateUtc(current);
    push('daily', {
      period: currentDateStr as unknown as EmissionsPeriod,
      start: currentDateStr,
      end: currentDateStr,
    });

    if (current.getUTCDay() === 0) {
      const weekEnd = new Date(current);
      weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
      const weekEndStr = dateUtc(weekEnd);
      push('weekly', {
        period: `${currentDateStr}-${weekEndStr}` as EmissionsPeriod,
        start: currentDateStr,
        end: weekEndStr,
      });
    }

    if (current.getUTCDate() === 1) {
      const monthEnd = new Date(current);
      monthEnd.setUTCMonth(monthEnd.getUTCMonth() + 1);
      monthEnd.setUTCDate(0);
      push('monthly', {
        period: currentDateStr.slice(0, 7) as EmissionsPeriod,
        start: currentDateStr,
        end: dateUtc(monthEnd),
      });

      const month = current.getUTCMonth();
      if (month === 0 || month === 3 || month === 6 || month === 9) {
        const quarterEnd = new Date(current);
        quarterEnd.setUTCMonth(quarterEnd.getUTCMonth() + 3);
        quarterEnd.setUTCDate(0);
        const quarterEndStr = dateUtc(quarterEnd);
        push('quarterly', {
          period: `${currentDateStr}-${quarterEndStr}` as EmissionsPeriod,
          start: currentDateStr,
          end: quarterEndStr,
        });
      }

      if ((month === 0 || month === 6) && current.getUTCDate() === 1) {
        const semiannualEnd = new Date(current);
        semiannualEnd.setUTCMonth(semiannualEnd.getUTCMonth() + 6);
        semiannualEnd.setUTCDate(0);
        const semiannualEndStr = dateUtc(semiannualEnd);
        push('semiannually', {
          period: `${currentDateStr}-${semiannualEndStr}` as EmissionsPeriod,
          start: currentDateStr,
          end: semiannualEndStr,
        });
      }

      if (month === 0 && current.getUTCDate() === 1) {
        const annualEnd = new Date(current);
        annualEnd.setUTCMonth(11);
        annualEnd.setUTCDate(31);
        const annualEndStr = dateUtc(annualEnd);
        push('annually', {
          period: currentDateStr.slice(0, 4) as EmissionsPeriod,
          start: currentDateStr,
          end: annualEndStr,
        });
      }
    }
  }
}

export function* periodsProduce(
  frequency: EmissionsTrackingFrequency,
  start: ISO8601DateOnly,
  end: ISO8601DateOnly,
): Generator<EmissionsPeriodDetailsNormal, void, void> {
  for (const item of periodsPrecomputed[frequency]) {
    // We flip-flop comparing against start/end below so that requesting weekly periods for a particular month will
    // return all weeks that are involved in that month, even if the start/end dates of those periods lie outside the
    // month:
    if (item.end >= start && item.start <= end) {
      yield item;
    }
  }
}

function* periodsRolling(period: EmissionsPeriodDetailsNormal): Generator<EmissionsPeriodDetailsNormal, void, void> {
  let count: number;
  if (period.frequency === 'daily') {
    count = 365;
  } else if (period.frequency === 'weekly') {
    count = 52;
  } else if (period.frequency === 'monthly') {
    count = 12;
  } else if (period.frequency === 'quarterly') {
    count = 4;
  } else if (period.frequency === 'semiannually') {
    count = 2;
  } else if (period.frequency === 'annually') {
    count = 1;
  } else {
    const freq: never = period.frequency;
    console.error('periodsRolling: Unexpected frequency:', freq);
    count = 1;
  }

  const indexStart = Math.max(0, period.index - (count - 1));
  for (let i = indexStart; i <= period.index; i++) {
    yield periodsPrecomputed[period.frequency][i];
  }
}

function periodToRollingRange(period: EmissionsPeriodDetailsNormal): string {
  const rollingStart = periodsRolling(period).next().value!.friendly.split(' - ')[0];
  const rollingEnd = period.friendly.split(' - ').slice(-1)[0];
  return `${rollingStart} - ${rollingEnd}`;
}
